"use client";

import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { Navbar, Footer } from "@/components/layout";
import NotificationBar from "@/components/layout/NotificationBar";
import { getNotificationConfigFromSanity, defaultNotificationConfig } from "@/config/appConfig";

export default function HomeLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isHomePage = pathname === "/";
  
  // State for notification config from Sanity
  const [notificationConfig, setNotificationConfig] = useState(defaultNotificationConfig);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  // Fetch notification config from Sanity
  useEffect(() => {
    const fetchNotificationConfig = async () => {
      try {
        const config = await getNotificationConfigFromSanity();
        setNotificationConfig(config);
      } catch (error) {
        console.error('Error loading notification config:', error);
        setNotificationConfig(defaultNotificationConfig);
      } finally {
        setIsConfigLoaded(true);
      }
    };

    fetchNotificationConfig();
  }, []);

  const configShowsBar = notificationConfig.showNotificationBar;
  const [isBarVisible, setIsBarVisible] = useState(true);

  useEffect(() => {
    if (!isHomePage || !configShowsBar) {
      return;
    }

    const handleScroll = () => {
      setIsBarVisible(window.scrollY < 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage, configShowsBar]);

  const shouldShowNotificationBar = isHomePage && configShowsBar;
  const isNotificationBarActuallyVisible = shouldShowNotificationBar && isBarVisible;

  const isContactUs = pathname === "/book-demo";
  const isGetStarted = pathname === "/get-started";
  const isMumbai = pathname === "/events/2025/agentic-demo-day";
  const isNewsletter = pathname === "/newsletter";

  if (isContactUs || isMumbai || isNewsletter || isGetStarted) {
    // Render only the page content for specific pages
    return <>{children}</>;
  }

  return (
    <main className="bg-background-2">
      {isConfigLoaded && shouldShowNotificationBar && (
        <NotificationBar isVisible={isBarVisible} config={notificationConfig} />
      )}
      <div className="relative z-50">
        <Navbar isNotificationBarVisible={isNotificationBarActuallyVisible} />
      </div>
      {children}
      <Footer />
    </main>
  );
} 